import React, { useState, useEffect, useMemo } from 'react';
import { useDebouncedCallback } from '../../hooks/useDebounce';

import {
  FaSearch,
  FaPlus,
  FaEdit,
  FaTrash,
  FaClock,
  FaProjectDiagram,
  FaCalendarAlt,
  FaFileExcel,
  FaCheckCircle,
  FaTimesCircle,
  FaUsers,
  FaMapMarkerAlt,
  FaRupeeSign,
  FaBuilding,
  FaTasks,
  FaUser,
  FaExclamationCircle,
  FaPause,
  FaPlay,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaClipboardList
} from 'react-icons/fa';
import { FiFilter, FiX, FiUsers, FiMapPin, FiSettings } from 'react-icons/fi';

import { Pagination } from '../../utils/pagination.utils';
import { ExcelDownload } from '../../utils/excel.utils';
import { useAuth } from '../../provider/auth.provider';
import { LuEye } from 'react-icons/lu';
import { Link } from 'react-router-dom';
import api from '../../libs/axios';
import { SelectWithSearch } from '../../utils/select-with-search';
import toast from 'react-hot-toast';

interface Activity {
  id: number;
  activity_name: string;
  activity_short_name: string;
  activity_description?: string;
  status?: string;
}

export interface ProjectFilters {
  client_id?: string;
  activity_id?: string;
  state_circle_id?: string;
  assigned_user_ids?: number[];
  status?: string;
  budget?: string;
}

interface Client {
  id: number;
  client_full_name: string;
  client_short_name: string;
  address?: string;
  gst_number?: string;
}

interface StateCircle {
  id: number;
  state_name: string;
  circle_name?: string | null;
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
}

interface ProjectSite {
  id: number;
  site_name: string;
  status: string;
  assigned_engineer?: User;
}

interface UserProject {
  id: number;
  user_id: number;
  project_id: number;
  user?: User;
}

interface Project {
  id: number;
  activity_id: number;
  client_id: number;
  state_circle_id: number;
  client_project_id?: number;
  project_name: string;
  project_short_name: string;
  project_description?: string;
  budget?: number;
  nearby_site_radius_km?: number | null;
  inc_checkin_without_verification?: boolean;
  status: string;
  created_by: number;
  created_at: string;
  updated_at: string;
  activity?: Activity;
  client?: Client;
  state_circle?: StateCircle;
  client_project?: ClientProject;
  user_projects?: UserProject[];
  sites?: ProjectSite[];
}

interface ClientProject {
  id: number;
  project_name: string;
}

type ProjectExportRow = Record<string, string | number | boolean | null>;

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters: ProjectFilters;
  onFiltersChange: (filters: ProjectFilters) => void;
  onApply: () => void;
  onReset: () => void;
  clients: Client[];
  activities: Activity[];
  stateCircles: StateCircle[];
  users: User[];
}

const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  onApply,
  onReset,
  clients,
  activities,
  stateCircles,
  users
}) => {
  const [activeTab, setActiveTab] = useState<'details' | 'team' | 'status'>('details');
  const [teamSearchTerm, setTeamSearchTerm] = useState('');
  const [teamSearchResults, setTeamSearchResults] = useState<User[]>([]);
  const [teamSearchLoading, setTeamSearchLoading] = useState(false);
  const [teamSearchError, setTeamSearchError] = useState<string | null>(null);
  const [selectedUsersMap, setSelectedUsersMap] = useState<Record<number, User>>({});

  const clientOptions = useMemo(
    () =>
      clients.map(client => ({
        id: client.id,
        label: client.client_full_name,
        value: client.id
      })),
    [clients]
  );

  const activityOptions = useMemo(
    () =>
      activities.map(activity => ({
        id: activity.id,
        label: activity.activity_name,
        value: activity.id
      })),
    [activities]
  );

  const stateOptions = useMemo(
    () =>
      stateCircles.map(state => ({
        id: state.id,
        label: state.state_name,
        value: state.id
      })),
    [stateCircles]
  );

  const selectedClient = useMemo(() => {
    if (!filters.client_id) return null;
    return (
      clientOptions.find(option => String(option.value) === String(filters.client_id)) || null
    );
  }, [clientOptions, filters.client_id]);

  const selectedActivity = useMemo(() => {
    if (!filters.activity_id) return null;
    return (
      activityOptions.find(option => String(option.value) === String(filters.activity_id)) || null
    );
  }, [activityOptions, filters.activity_id]);

  const selectedState = useMemo(() => {
    if (!filters.state_circle_id) return null;
    return (
      stateOptions.find(option => String(option.value) === String(filters.state_circle_id)) || null
    );
  }, [stateOptions, filters.state_circle_id]);

  useEffect(() => {
    if (!isOpen) {
      setActiveTab('details');
      setTeamSearchTerm('');
      setTeamSearchResults([]);
      setTeamSearchError(null);
      setTeamSearchLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!users || users.length === 0) return;

    setSelectedUsersMap((prev) => {
      const next = { ...prev };
      users.forEach((user) => {
        next[user.id] = user;
      });
      return next;
    });
  }, [users]);

  useEffect(() => {
    const term = teamSearchTerm.trim();

    if (term.length < 3) {
      setTeamSearchResults([]);
      setTeamSearchError(null);
      setTeamSearchLoading(false);
      return;
    }

    let cancelled = false;
    setTeamSearchLoading(true);
    setTeamSearchError(null);

    const timeoutId = setTimeout(async () => {
      try {
        const response = await api.get('/projects/team/search', {
          params: {
            search: term,
            page: 1,
            limit: 10
          }
        });

        if (cancelled) {
          return;
        }

        if (response.data?.success) {
          const payload = response.data?.data;
          const extractedUsers: User[] = Array.isArray(payload)
            ? payload
            : Array.isArray(payload?.users)
              ? payload.users
              : Array.isArray(payload?.results)
                ? payload.results
                : [];

          setTeamSearchResults(extractedUsers);
          if (extractedUsers.length) {
            setSelectedUsersMap((prev) => {
              const next = { ...prev };
              extractedUsers.forEach((user) => {
                next[user.id] = user;
              });
              return next;
            });
          }
        } else {
          setTeamSearchError(response.data?.message || 'Unable to search team members.');
          setTeamSearchResults([]);
        }
      } catch (error: any) {
        if (!cancelled) {
          setTeamSearchError(error?.response?.data?.message || 'Unable to search team members.');
          setTeamSearchResults([]);
        }
      } finally {
        if (!cancelled) {
          setTeamSearchLoading(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [teamSearchTerm]);

  if (!isOpen) return null;

  const handleInputChange = (field: keyof ProjectFilters, value: string | number | undefined) => {
    onFiltersChange({
      ...filters,
      [field]: value || undefined
    });
  };

  const handleUserToggle = (userId: number, user?: User) => {
    const current = filters.assigned_user_ids || [];
    const isSelected = current.includes(userId);
    const updated = isSelected ? current.filter((id) => id !== userId) : [...current, userId];

    onFiltersChange({
      ...filters,
      assigned_user_ids: updated.length ? updated : undefined
    });

    if (user) {
      setSelectedUsersMap((prev) => ({
        ...prev,
        [userId]: user
      }));
    }
  };

  const tabs = [
    { id: 'details', label: 'Client & Activity', icon: FiMapPin },
    { id: 'team', label: 'Team Members', icon: FiUsers },
    { id: 'status', label: 'Status & Budget', icon: FiSettings }
  ] as const;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] min-h-[520px] flex flex-col overflow-visible">

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-700 to-blue-900 text-white flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FiFilter className="w-5 h-5" />
            Filters
          </h3>
          <button onClick={onClose} className="p-2 rounded hover:bg-white/10 transition-colors">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-gray-50 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600 bg-white'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">

          {activeTab === 'details' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Client</label>
                <SelectWithSearch
                  placeholder="All Clients"
                  value={selectedClient}
                  onChange={(option) => handleInputChange('client_id', option ? String(option.value) : undefined)}
                  options={clientOptions}
                  className="w-full"
                  closeOnOutsideClick={false}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Activity</label>
                <SelectWithSearch
                  placeholder="All Activities"
                  value={selectedActivity}
                  onChange={(option) => handleInputChange('activity_id', option ? String(option.value) : undefined)}
                  options={activityOptions}
                  className="w-full"
                  closeOnOutsideClick={false}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">State / Circle</label>
                <SelectWithSearch
                  placeholder="All States"
                  value={selectedState}
                  onChange={(option) => handleInputChange('state_circle_id', option ? String(option.value) : undefined)}
                  options={stateOptions}
                  className="w-full"
                  closeOnOutsideClick={false}
                />
              </div>
            </div>
          )}

          {activeTab === 'team' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search Team Members</label>
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={teamSearchTerm}
                    onChange={(e) => setTeamSearchTerm(e.target.value)}
                    placeholder="Type at least 3 characters to search by name or email"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Results appear after entering 3 or more characters.</p>
              </div>

              {teamSearchTerm.trim().length < 3 ? (
                <div className="px-4 py-3 text-sm text-gray-600 bg-gray-50 border border-dashed border-gray-200 rounded">
                  Start typing at least three characters to search for team members.
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  {teamSearchLoading && (
                    <p className="text-sm text-gray-500 p-4">Searching team members...</p>
                  )}
                  {!teamSearchLoading && teamSearchError && (
                    <p className="text-sm text-red-600 p-4">{teamSearchError}</p>
                  )}
                  {!teamSearchLoading && !teamSearchError && teamSearchResults.length === 0 && (
                    <p className="text-sm text-gray-500 p-4">No users found for “{teamSearchTerm.trim()}”.</p>
                  )}
                  {!teamSearchLoading && !teamSearchError && teamSearchResults.length > 0 && (
                    <div className="max-h-64 overflow-y-auto divide-y">
                      {teamSearchResults.map((user) => (
                        <label key={user.id} className="flex items-start gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50">
                          <input
                            type="checkbox"
                            checked={filters.assigned_user_ids?.includes(user.id) || false}
                            onChange={() => handleUserToggle(user.id, user)}
                            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="flex-1">
                            <span className="font-medium text-gray-900 block">
                              {user.first_name} {user.last_name}
                            </span>
                            {user.email && <span className="text-xs text-gray-500">{user.email}</span>}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {filters.assigned_user_ids && filters.assigned_user_ids.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Selected Users</p>
                  <div className="flex flex-wrap gap-2">
                    {filters.assigned_user_ids.map((userId) => {
                      const userInfo = selectedUsersMap[userId];
                      const label = userInfo
                        ? `${userInfo.first_name} ${userInfo.last_name}`
                        : `User ID: ${userId}`;
                      return (
                        <span
                          key={userId}
                          className="inline-flex items-center gap-2 px-3 py-1 text-xs font-medium bg-blue-50 text-blue-800 border border-blue-100 rounded-full"
                        >
                          {label}
                          <button
                            type="button"
                            onClick={() => handleUserToggle(userId)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <FiX className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'status' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={filters.status || ''}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="">All Statuses</option>
                  <option value="ACTIVE">Active</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="ON_HOLD">On Hold</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Budget (Min)</label>
                <input
                  type="number"
                  min={0}
                  placeholder="Enter minimum budget"
                  value={filters.budget || ''}
                  onChange={(e) => handleInputChange('budget', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center">
          <button
            onClick={() => {
              onReset();
              onClose();
            }}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            Reset Filters
          </button>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onApply();
                onClose();
              }}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-700 rounded hover:bg-blue-800 transition-colors flex items-center gap-2"
            >
              <FaSearch className="w-4 h-4" />
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ProjectsListProps {
  projects: Project[];
  pagination: Pagination;
  onSearch: (search: string) => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onAddProject?: () => void;
  hideAddProjectButton?: boolean;
  onEditProject: (project: Project) => void;
  onDeleteProject: (project: Project) => void;
  clients: Client[];
  activities: Activity[];
  stateCircles: StateCircle[];
  users: User[];
  onAdvancedFilter?: (filters: ProjectFilters) => void;
  advancedFilters?: ProjectFilters;
  loading?: boolean;
  actionLoading?: boolean;
  onSort?: (sortBy: string, sortOrder: 'ASC' | 'DESC') => void;
  onRequirementsClick?: (project: Project) => void;
  /** Called after a successful bulk update so the parent can refetch the page. */
  onBulkUpdated?: () => void;
}

export const ProjectsList: React.FC<ProjectsListProps> = ({
  projects,
  pagination,
  onSearch,
  onPageChange,
  onLimitChange,
  onAddProject,
  hideAddProjectButton = false,
  onEditProject,
  onDeleteProject,
  clients,
  activities,
  stateCircles,
  users,
  onAdvancedFilter,
  advancedFilters,
  loading = false,
  actionLoading = false,
  onSort,
  onRequirementsClick,
  onBulkUpdated,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState<ProjectFilters>({});
  const [exporting, setExporting] = useState(false);
  const [exportData, setExportData] = useState<ProjectExportRow[]>([]);
  const [sortConfig, setSortConfig] = useState<{
    field: string;
    direction: 'ASC' | 'DESC';
  }>({
    field: 'created_at',
    direction: 'DESC'
  });
  // Bulk nearby-radius update. Selection is kept across pages by id, so a set
  // built from several pages can be updated in one call.
  const [selectedProjectIds, setSelectedProjectIds] = useState<number[]>([]);
  const [showRadiusModal, setShowRadiusModal] = useState(false);
  const [radiusValue, setRadiusValue] = useState('');
  const [savingRadius, setSavingRadius] = useState(false);
  // Projects the signed-in user is assigned to. Bulk editing is restricted to
  // these (role level 1 excepted) — the same rule the API enforces, mirrored
  // here so the checkbox is simply not offered for anything out of reach.
  const [assignedProjectIds, setAssignedProjectIds] = useState<number[] | null>(null);

  useEffect(() => {
    setFilters(advancedFilters || {});
  }, [advancedFilters]);

  const { hasPermission, user } = useAuth();

  const isLevelOneUser = user?.role?.level === 1;
  const canBulkEditRadius = hasPermission('Project', 'edit');

  useEffect(() => {
    if (!canBulkEditRadius || isLevelOneUser) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await api.get('/users/my-projects');
        const rows = response.data?.data?.projects || [];
        const ids = rows
          .map((row: any) => Number(row.project_id ?? row.project?.id))
          .filter((id: number) => Number.isFinite(id));
        if (!cancelled) setAssignedProjectIds(ids);
      } catch (error) {
        // A caller whose assignments cannot be read gets no bulk selection at
        // all, rather than a selection the API would reject anyway.
        if (!cancelled) setAssignedProjectIds([]);
      }
    })();
    return () => { cancelled = true; };
  }, [canBulkEditRadius, isLevelOneUser]);

  const isProjectSelectable = (projectId: number) => {
    if (!canBulkEditRadius) return false;
    if (isLevelOneUser) return true;
    return (assignedProjectIds ?? []).includes(projectId);
  };

  const selectableProjectIds = useMemo(
    () => projects.filter((project) => isProjectSelectable(project.id)).map((project) => project.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projects, assignedProjectIds, canBulkEditRadius, isLevelOneUser]
  );

  const allOnPageSelected =
    selectableProjectIds.length > 0 &&
    selectableProjectIds.every((id) => selectedProjectIds.includes(id));

  const toggleProjectSelection = (projectId: number) => {
    setSelectedProjectIds((prev) =>
      prev.includes(projectId) ? prev.filter((id) => id !== projectId) : [...prev, projectId]
    );
  };

  const togglePageSelection = () => {
    setSelectedProjectIds((prev) =>
      allOnPageSelected
        ? prev.filter((id) => !selectableProjectIds.includes(id))
        : prev.concat(selectableProjectIds.filter((id) => !prev.includes(id)))
    );
  };

  const handleBulkRadiusSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const radius = Number(radiusValue);
    if (!Number.isFinite(radius) || radius <= 0 || radius > 5000) {
      toast.error('Enter a radius between 0 and 5000 km');
      return;
    }

    setSavingRadius(true);
    try {
      const response = await api.put('/projects/bulk/nearby-radius', {
        project_ids: selectedProjectIds,
        nearby_site_radius_km: radius,
      });
      const updated = response.data?.data?.updated_count ?? 0;
      const skipped = response.data?.data?.skipped_project_ids?.length ?? 0;
      toast.success(
        `Nearby radius set to ${radius} km on ${updated} project${updated === 1 ? '' : 's'}` +
        (skipped ? ` — ${skipped} skipped (not in your projects)` : '')
      );
      setShowRadiusModal(false);
      setRadiusValue('');
      setSelectedProjectIds([]);
      onBulkUpdated?.();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to update nearby radius');
    } finally {
      setSavingRadius(false);
    }
  };

  const debouncedOnSearch = useDebouncedCallback(onSearch, 400)

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedOnSearch(value);
  };

  const handleApplyFilters = () => {
    onAdvancedFilter?.(filters);
  };

  const handleResetFilters = () => {
    setFilters({});
    onAdvancedFilter?.({});
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).reduce((count, value) => {
      if (Array.isArray(value)) {
        return value.length ? count + 1 : count;
      }
      return value ? count + 1 : count;
    }, 0);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'ACTIVE': {
        icon: FaCheckCircle,
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        label: 'Active'
      },
      'COMPLETED': {
        icon: FaCheckCircle,
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        label: 'Completed'
      },
      'ON_HOLD': {
        icon: FaPause,
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        label: 'On Hold'
      },
      'CANCELLED': {
        icon: FaTimesCircle,
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        label: 'Cancelled'
      }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || {
      icon: FaExclamationCircle,
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-800',
      label: status
    };

    const IconComponent = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}>
        <IconComponent className="w-2 h-2" />
        {config.label}
      </span>
    );
  };

  const calculateProgress = (sites: ProjectSite[] = []) => {
    if (sites.length === 0) return 0;
    const completedSites = sites.filter(site => site.status === 'REPORT_SUBMITTED').length;
    return Math.round((completedSites / sites.length) * 100);
  };

  const mapProjectForExport = (project: Project): ProjectExportRow => {
    return {
      'Project Name': project.project_name,
      'Short Name': project.project_short_name,
      'Client': project.client?.client_short_name || 'N/A',
      'Activity': project.activity?.activity_short_name || 'N/A',
      'State/Circle': project.state_circle?.state_name || 'N/A',
      'Status': project.status,
      'Budget': project.budget ? formatCurrency(project.budget) : 'N/A',
      'Nearby Sites Radius (km)': project.nearby_site_radius_km ?? 'N/A',
      'INC Check-in Without Verification': project.inc_checkin_without_verification === false ? 'No' : 'Yes',
      'Total Sites': project.sites?.length || 0,
      'Progress': `${calculateProgress(project.sites)}%`,
      'Created Date': formatDate(project.created_at),
      'Last Updated': formatDate(project.updated_at),
      'Project Description': project.project_description || 'N/A',
      'Client Full Name': project.client?.client_full_name || 'N/A',
      'Client Address': project.client?.address || 'N/A',
      'Client GST Number': project.client?.gst_number || 'N/A',
      'Activity Description': project.activity?.activity_description || 'N/A',
      'Activity Status': project.activity?.status || 'N/A',
      'Circle Name': project.state_circle?.circle_name || 'N/A',
      'Assigned Team Members': project.user_projects && project.user_projects.length
        ? project.user_projects
            .map(up => (up.user ? `${up.user.first_name} ${up.user.last_name}`.trim() : ''))
            .filter(Boolean)
            .join(', ')
        : 'N/A',
      'Assigned Team Count': project.user_projects?.length ?? 0,
    };
  };

  const buildExportParams = (page: number, limit: number) => {
    const params: Record<string, string | number> = {
      page,
      limit,
    };

    const trimmedSearch = searchTerm.trim();
    if (trimmedSearch) {
      params.search = trimmedSearch;
    }

    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
        return;
      }

      if (Array.isArray(value)) {
        params[key] = value.join(',');
        return;
      }

      params[key] = value as string | number;
    });

    if (sortConfig.field) {
      params.sort_by = sortConfig.field;
      params.sort_order = sortConfig.direction;
    }

    return params;
  };

  const fetchExportData = async () => {
    if (exporting) return;

    try {
      setExporting(true);
      setExportData([]);

      const pageSize = 100;
      let page = 1;
      let hasMore = true;
      const allProjects: Project[] = [];

      while (hasMore) {
        const params = buildExportParams(page, pageSize);
        const response = await api.get('/projects/list', { params });

        if (!response.data?.success) {
          throw new Error(response.data?.message || 'Failed to fetch projects for export');
        }

        const data = response.data.data || {};
        const fetched: Project[] = data.projects || [];
        const paginationInfo = data.pagination;

        allProjects.push(...fetched);

        const nextPageAvailable =
          paginationInfo?.has_next_page ??
          (paginationInfo?.total_pages ? page < paginationInfo.total_pages : fetched.length === pageSize);

        if (!fetched.length || !nextPageAvailable) {
          hasMore = false;
        } else {
          page += 1;
        }
      }

      if (!allProjects.length) {
        toast.error('No projects found for export');
        return;
      }

      setExportData(allProjects.map(mapProjectForExport));
      setShowExcelModal(true);
    } catch (error: any) {
      console.error('Error exporting projects:', error);
      toast.error(error?.response?.data?.message || 'Failed to prepare export data');
    } finally {
      setExporting(false);
    }
  };

  const handleSort = (field: string) => {
    const direction = sortConfig.field === field && sortConfig.direction === 'ASC' ? 'DESC' : 'ASC';
    setSortConfig({ field, direction });
    onSort?.(field, direction);
  };

  const getSortIcon = (field: string) => {
    if (sortConfig.field !== field) return <FaSort className="w-3 h-3" />;
    return sortConfig.direction === 'ASC' ? (
      <FaSortUp className="w-3 h-3" />
    ) : (
      <FaSortDown className="w-3 h-3" />
    );
  };

  return (
    <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-xl font-semibold text-gray-900">Project Management</h2>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            {canBulkEditRadius && selectedProjectIds.length > 0 && (
              <button
                onClick={() => setShowRadiusModal(true)}
                className="bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
              >
                <FiMapPin className="w-4 h-4" />
                Set Nearby Radius ({selectedProjectIds.length})
              </button>
            )}

            <button
              onClick={fetchExportData}
              disabled={exporting}
              className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
            >
              <FaFileExcel className="w-4 h-4" />
              {exporting ? 'Preparing...' : 'Export Excel'}
            </button>

            {/* Hidden where the list is embedded (the Requirements screens). */}
            {onAddProject && !hideAddProjectButton && hasPermission('Project', 'create') && (
              <button
                onClick={onAddProject}
                className="bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
              >
                <FaPlus className="w-4 h-4" />
                Add New Project
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/30">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Search Projects</label>
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
              <input
                type="text"
                placeholder="Search by project name or description"
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div>

          {/* Advanced Filters Button */}
          <div className="w-full sm:w-auto">
            <label className="block text-xs font-medium text-gray-700 mb-1">&nbsp;</label>
            <button
              type="button"
              onClick={() => setIsFilterModalOpen(true)}
              className="w-full sm:w-auto px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 flex items-center justify-center gap-2 transition-colors text-sm relative"
            >
              <FiFilter className="w-4 h-4" />
              Filters
              {getActiveFiltersCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-blue-800 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {getActiveFiltersCount()}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {canBulkEditRadius && selectedProjectIds.length > 0 && (
        <div className="px-4 sm:px-6 py-2 bg-blue-50 border-b border-blue-200 flex flex-wrap items-center gap-3 text-sm">
          <span className="font-medium text-blue-900">
            {selectedProjectIds.length} project{selectedProjectIds.length === 1 ? '' : 's'} selected
          </span>
          <button
            onClick={() => setSelectedProjectIds([])}
            className="text-blue-700 hover:text-blue-900 underline"
          >
            Clear selection
          </button>
          <span className="text-blue-700/70">
            Selection is kept while you search or change pages.
          </span>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {canBulkEditRadius && (
                <th className="px-4 py-3 text-left w-10">
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    onChange={togglePageSelection}
                    disabled={selectableProjectIds.length === 0}
                    title={
                      selectableProjectIds.length === 0
                        ? 'None of the projects on this page are yours to edit'
                        : 'Select every project on this page'
                    }
                    className="h-4 w-4 rounded border-gray-300 text-blue-700 focus:ring-blue-500 disabled:opacity-40"
                  />
                </th>
              )}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                Actions
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[200px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('project_name')}
              >
                <div className="flex items-center gap-1">
                  Project Name
                  {getSortIcon('project_name')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('project_short_name')}
              >
                <div className="flex items-center gap-1">
                  Short Name
                  {getSortIcon('project_short_name')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[200px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('project_description')}
              >
                <div className="flex items-center gap-1">
                  Description
                  {getSortIcon('project_description')}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                Client
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                Activity
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                State/Circle
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                Client Project
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('budget')}
              >
                <div className="flex items-center gap-1">
                  Budget
                  {getSortIcon('budget')}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[130px]">
                Nearby Radius (km)
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[170px]">
                INC Check-in
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center gap-1">
                  Status
                  {getSortIcon('status')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('created_at')}
              >
                <div className="flex items-center gap-1">
                  Created At
                  {getSortIcon('created_at')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('updated_at')}
              >
                <div className="flex items-center gap-1">
                  Updated At
                  {getSortIcon('updated_at')}
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={canBulkEditRadius ? 18 : 17} className="px-4 sm:px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                    <span className="text-sm text-gray-600">Loading projects...</span>
                  </div>
                </td>
              </tr>
            ) : projects.length === 0 ? (
              <tr>
                <td colSpan={canBulkEditRadius ? 18 : 17} className="px-4 sm:px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <FaProjectDiagram className="w-6 h-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No projects found</p>
                    <p className="text-xs text-gray-400 mt-1">Try adjusting your search or create a new project</p>
                  </div>
                </td>
              </tr>
            ) : (
              projects.map((project) => (
                <tr key={project.id} className="hover:bg-gray-50 transition-colors">
                  {canBulkEditRadius && (
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedProjectIds.includes(project.id)}
                        onChange={() => toggleProjectSelection(project.id)}
                        disabled={!isProjectSelectable(project.id)}
                        title={
                          isProjectSelectable(project.id)
                            ? 'Select for bulk radius update'
                            : 'You are not assigned to this project'
                        }
                        className="h-4 w-4 rounded border-gray-300 text-blue-700 focus:ring-blue-500 disabled:opacity-40"
                      />
                    </td>
                  )}

                  {/* Actions */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      {hasPermission('Project', 'view') && (
                        <Link
                          target="_blank"
                          to={`/projects/view/${project.id}`}
                          className={`text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded transition-colors ${actionLoading ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                          title="View project"
                        >
                          <LuEye className="w-4 h-4" />
                        </Link>
                      )}
                      {hasPermission('Project', 'edit') && !onRequirementsClick && (
                        <button
                          onClick={() => onEditProject(project)}
                          disabled={actionLoading}
                          className={`text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded transition-colors ${actionLoading ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                          title="Edit project"
                        >
                          <FaEdit className="w-4 h-4" />
                        </button>
                      )}
                      {onRequirementsClick && (
                        <button
                          onClick={() => onRequirementsClick(project)}
                          disabled={actionLoading}
                          className={`text-orange-600 hover:text-orange-800 p-2 hover:bg-orange-50 rounded transition-colors ${actionLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          title="Client Requirements"
                        >
                          <FaClipboardList className="w-4 h-4" />
                        </button>
                      )}
                      {/* {hasPermission('Project', 'delete') && (
                        <button
                          onClick={() => onDeleteProject(project)}
                          disabled={actionLoading}
                          className={`text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded transition-colors ${actionLoading ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                          title="Delete project"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      )} */}
                    </div>
                  </td>

                  {/* Project Name */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">
                        {project.project_name}
                      </span>
                    </div>
                  </td>

                  {/* Short Name */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-blue-600 font-medium">
                      {project.project_short_name}
                    </span>
                  </td>

                  {/* Description */}
                  <td className="px-4 py-4">
                    <span className="text-sm text-gray-600">
                      {project.project_description ? (
                        <span title={project.project_description} className="truncate max-w-[180px] block">
                          {project.project_description}
                        </span>
                      ) : (
                        <span className="text-gray-400 italic">No description</span>
                      )}
                    </span>
                  </td>

                  {/* Client */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900">
                        {project.client?.client_short_name || <span className="text-gray-400 italic">N/A</span>}
                      </span>
                    </div>
                  </td>

                  {/* Activity */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900">
                        {project.activity?.activity_name || <span className="text-gray-400 italic">N/A</span>}
                      </span>
                    </div>
                  </td>

                  {/* State/Circle */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900">
                        {project.state_circle?.state_name || <span className="text-gray-400 italic">N/A</span>}
                      </span>
                    </div>
                  </td>

                  {/* Client Project */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600">
                      {project.client_project?.project_name || <span className="text-gray-400 italic">N/A</span>}
                    </span>
                  </td>

                  {/* Budget */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    {project.budget ? (
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">
                          {formatCurrency(project.budget)}
                        </span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400 italic">N/A</span>
                    )}
                  </td>

                  {/* Nearby Sites Radius */}
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {project.nearby_site_radius_km !== null && project.nearby_site_radius_km !== undefined
                      ? `${Number(project.nearby_site_radius_km)} km`
                      : <span className="text-gray-400 italic">N/A</span>}
                  </td>

                  {/* INC: is verification approval needed before visit check-in? */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    {project.inc_checkin_without_verification === false ? (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
                        Verification required
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                        Without verification
                      </span>
                    )}
                  </td>

                  {/* Status */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    {getStatusBadge(project.status)}
                  </td>

                  {/* Created At */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        {formatDate(project.created_at)}
                      </span>
                    </div>
                  </td>

                  {/* Updated At */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        {formatDate(project.updated_at)}
                      </span>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        pagination={pagination as any}
        onPageChange={onPageChange}
        onLimitChange={onLimitChange}
      />

      {/* Excel Download Modal */}
      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={exportData}
        filename="projects"
        title="Export Projects Data"
      />

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filters}
        onFiltersChange={setFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        clients={clients}
        activities={activities}
        stateCircles={stateCircles}
        users={users}
      />

      {/* Bulk nearby-radius update. One radius, applied to every selected
          project — the radius the mobile app uses to decide which sites an
          engineer is offered. */}
      {showRadiusModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md bg-white rounded shadow-xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Set Nearby Radius</h3>
              <button
                onClick={() => setShowRadiusModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleBulkRadiusSubmit} className="p-5 space-y-4">
              <p className="text-sm text-gray-600">
                This will replace the nearby-sites radius on{' '}
                <span className="font-semibold text-gray-900">
                  {selectedProjectIds.length} selected project
                  {selectedProjectIds.length === 1 ? '' : 's'}
                </span>
                .
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nearby Radius (km) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min={0.1}
                  max={5000}
                  step="0.1"
                  value={radiusValue}
                  onChange={(e) => setRadiusValue(e.target.value)}
                  placeholder="e.g. 30"
                  autoFocus
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Greater than 0 and at most 5000 km.
                </p>
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setShowRadiusModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-50 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingRadius || !radiusValue}
                  className="flex-1 bg-blue-800 hover:bg-blue-950 disabled:bg-blue-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded text-sm font-medium"
                >
                  {savingRadius ? 'Updating...' : 'Update Radius'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
